import { ScoringPriorityField } from '@graphql/generated';
import { EventVenue } from '@shared/utils/eventPlace';
import { EventVenueState } from '../components/EventVenueDialog/useEventVenueDialog';
import { AccommodationPropertyWithCalculatedPrice } from '@shared/hooks/useSearchExtendedAccommodationProperties';
import { PICKUP_BEST_HOTELS } from '../components/VenueInfo/VenueInfo.constants';
import { useImmerReducer } from 'use-immer';
import { computeDestination, sortAccommodationPropertiesWithCalculatedPrice } from '../components/EventVenueDialog/EventVenueDialog.utils';

export type BestAnchorLocation = {
  latitude: string;
  longitude: string;
  friendlyName: string;
};

type State = {
  eventVenueState: EventVenueState;
  newVenueInfo?: EventVenue;
  scoringPriorityFields: ScoringPriorityField[];
  bestAnchorLocation: BestAnchorLocation | null;
  destination: google.maps.LatLngLiteral;
  hotels: {
    data: AccommodationPropertyWithCalculatedPrice[];
    source: HotelSource;
    isLoading: boolean;
    hasError: boolean;
    allHotelCount: number;
    goodMatchedHotelsCount: number;
    bestHotelsCount: number;
  };
  shouldSearchForProperties: boolean;
};

export type HotelSource = 'search' | 'autoPopulated' | null;

export type VenueDialogAction =
  | { type: 'SET_EVENT_VENUE_STATE'; eventVenueState: EventVenueState }
  | { type: 'SET_NEW_VENUE_INFO'; venue: EventVenue }
  | { type: 'CLEAR_NEW_VENUE_INFO' }
  | { type: 'SET_SCORING_FIELDS'; scoringPriorityFields: ScoringPriorityField[] }
  | { type: 'SET_BEST_ANCHOR_LOCATION'; location: BestAnchorLocation | null }
  | {
      type: 'COMPUTE_AND_SET_DESTINATION';
      payload: {
        venue?: EventVenue;
        locationInfo: Maybe<{ latitude?: Maybe<number>; longitude?: Maybe<number> }>;
        bestAnchorLocation?: BestAnchorLocation | null;
      };
    }
  // Search hotels
  | { type: 'TRIGGER_PROPERTY_SEARCH' }
  | { type: 'SEARCH_FOR_PROPERTIES_STARTED' }
  | { type: 'SEARCH_FOR_PROPERTIES_ERROR' }
  | { type: 'SEARCH_FOR_PROPERTIES_SUCCESS'; accommodationPropertyWithCalculatedPrice: AccommodationPropertyWithCalculatedPrice[] }
  // Auto populate accommodations places
  | { type: 'AUTO_POPULATE_ACCOMMODATIONS_PLACES_STARTED' }
  | { type: 'AUTO_POPULATE_ACCOMMODATIONS_PLACES_ERROR' }
  | { type: 'AUTO_POPULATE_ACCOMMODATIONS_PLACES_SUCCESS'; accommodationPropertyWithCalculatedPrice: AccommodationPropertyWithCalculatedPrice[] };

const initialState: State = {
  eventVenueState: EventVenueState.create,
  scoringPriorityFields: [ScoringPriorityField.standardRoomPriceDiscountPercent, ScoringPriorityField.userRating, ScoringPriorityField.distanceFromSearchLocation],
  bestAnchorLocation: null,
  destination: { lat: 0, lng: 0 }, // If don't have venue, event and bestAnchor location then fallback to { lat: 0, lng: 0 }
  hotels: {
    data: [],
    source: null,
    isLoading: false,
    hasError: false,
    allHotelCount: 0,
    goodMatchedHotelsCount: 0,
    bestHotelsCount: 0,
  },
  shouldSearchForProperties: true,
};

export const useEventVenueDialogReducer = (initial: Partial<State> = {}) => {
  return useImmerReducer(reducer, { ...initialState, ...initial });
};

export const reducer = (state: State, action: VenueDialogAction) => {
  switch (action.type) {
    case 'SET_EVENT_VENUE_STATE':
      state.eventVenueState = action.eventVenueState;
      return;

    case 'SET_NEW_VENUE_INFO':
      state.newVenueInfo = action.venue;
      return;

    case 'CLEAR_NEW_VENUE_INFO':
      state.newVenueInfo = undefined;
      return;

    case 'SET_SCORING_FIELDS':
      state.scoringPriorityFields = action.scoringPriorityFields;
      return;

    case 'SET_BEST_ANCHOR_LOCATION':
      state.bestAnchorLocation = action.location;
      return;

    case 'COMPUTE_AND_SET_DESTINATION':
      state.destination = computeDestination(action.payload);
      return;

    // Auto populate accommodations places
    case 'AUTO_POPULATE_ACCOMMODATIONS_PLACES_STARTED':
      state.hotels.isLoading = true;
      return;

    case 'AUTO_POPULATE_ACCOMMODATIONS_PLACES_ERROR':
      state.hotels.isLoading = false;
      state.hotels.hasError = true;
      return;

    case 'AUTO_POPULATE_ACCOMMODATIONS_PLACES_SUCCESS': {
      const sortedAccommodationPropertiesWithCalculatedPrice = sortAccommodationPropertiesWithCalculatedPrice(
        action.accommodationPropertyWithCalculatedPrice,
        state.scoringPriorityFields[0],
      );

      state.hotels.data = sortedAccommodationPropertiesWithCalculatedPrice;
      state.hotels.bestHotelsCount = sortedAccommodationPropertiesWithCalculatedPrice.length;
      state.hotels.isLoading = false;
      return;
    }

    // Search hotels
    case 'TRIGGER_PROPERTY_SEARCH':
      state.shouldSearchForProperties = true;
      return;
    case 'SEARCH_FOR_PROPERTIES_STARTED':
      state.hotels.isLoading = true;
      return;

    case 'SEARCH_FOR_PROPERTIES_ERROR':
      state.hotels.isLoading = false;
      state.shouldSearchForProperties = false;
      state.hotels.hasError = true;
      return;

    case 'SEARCH_FOR_PROPERTIES_SUCCESS': {
      const hotels = action.accommodationPropertyWithCalculatedPrice;

      // All searched hotels count
      const allHotelCount = hotels.length;

      /**
       * Filtered to only include searched hotels with percentageOff
       * Less than 3 if hotels from search is 0, 1, or 2
       * Otherwise, at least 3 and equal to the size of goodMatchedHotels
       * If allHotelCount is less than 3 then goodMatchedHotelsCount would be all hotels count
       */
      const goodMatchedHotels = hotels.filter(h => h.percentageOff && h.percentageOff > 0);
      const goodMatchedHotelsCount = allHotelCount < PICKUP_BEST_HOTELS ? allHotelCount : Math.max(goodMatchedHotels.length, PICKUP_BEST_HOTELS);

      /**
       * Picked top 3 from goodMatchedHotels
       * Less than 3 if hotels from search is 0, 1, or 2
       * Otherwise, at least 3 and equal to the size of goodMatchedHotels
       */
      const bestHotelsCount = Math.min(goodMatchedHotelsCount, PICKUP_BEST_HOTELS);

      state.hotels = { data: hotels, source: 'search', isLoading: false, hasError: false, allHotelCount, goodMatchedHotelsCount, bestHotelsCount };
      state.shouldSearchForProperties = false;
      return;
    }

    default:
      return;
  }
};
